import React, { useState, useEffect, useRef } from "react"
import { useSelector } from "react-redux"
import { Modal, Form, Input, Button, notification } from "antd"
import { Autocomplete } from "@react-google-maps/api"
import { API, Endpoints } from "utils/api"
import Strings from "utils/strings"
import { GoogleMaps } from "components"

const AddressValidationModal: React.FC<{
	userId: string
	setShowAddressValidationModal: (userId: string) => void
	users: any[]
	refreshData: () => void
}> = ({ userId, setShowAddressValidationModal, users, refreshData }) => {
	const mobile = useSelector((state: any) => state.mobile)
	const [form] = Form.useForm()

	const user = users?.find((u: any) => u._id === userId)

	// State variables for fields that don't work with Form
	const [address, setAddress] = useState<string>("")
	const [coordinates, setCoordinates] = useState<any>({})
	const [userBoundingBox, setUserBoundingBox] = useState<any>(null)

	useEffect(() => {
		if (!userId || !user) return

		if (user?.address?.address) {
			setAddress(user.address.address)
		}
		if (user?.address?.location) {
			setCoordinates(user.address.location)
		}

		form.setFieldsValue({
			address: user.address?.address || "",
			addressNotes: user.address?.addressNotes || "",
			lat: user.address?.location?.lat || "",
			lng: user.address?.location?.lng || "",
			postalCode: user.address?.postalCode || "",
			city: user.address?.city || "",
			country: user.address?.country || "",
		})
	}, [userId, user, form])

	let userErrorsList: string[] = []
	if (user?.addressErrors) {
		userErrorsList = Object.entries(user.addressErrors)
			.map(([key, value]) => (value ? key : ""))
			.filter(Boolean)
	}

	const handleCancel = () => {
		setShowAddressValidationModal("")
		setAddress("")
		setCoordinates({})
		setUserBoundingBox(null)
		form.resetFields()
	}

	const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)

	const handlePlaceChanged = () => {
		const place = autocompleteRef.current?.getPlace()
		if (!place || !place.geometry) return

		const address = place.formatted_address || ""
		const lat = place.geometry.location?.lat()
		const lng = place.geometry.location?.lng()
		const postalCode = place.address_components?.find((component: any) =>
			component.types.includes("postal_code")
		)?.long_name
		const city = place.address_components?.find(
			(component: any) =>
				component.types.includes("locality") ||
				component.types.includes("administrative_area_level_2")
		)?.long_name
		const country = place.address_components?.find((component: any) =>
			component.types.includes("country")
		)?.long_name

		if (address) {
			form.setFieldsValue({
				address: address,
				lat,
				lng,
				postalCode,
				city,
				country,
			})
			setAddress(address)
			setCoordinates({
				lat,
				lng,
			})
		}
	}

	return (
		<Modal
			centered={true}
			closable={true}
			onCancel={handleCancel}
			visible={!!userId}
			footer={null}
			bodyStyle={{ padding: 0 }}
			width={mobile ? "100%" : 600}
		>
			<div className="UploadModal">
				<div className="UploadTitle">
					<p>
						Address Validation. User:{" "}
						<span style={{ fontWeight: "normal" }}>
							{user?.name || user?.importedId}
						</span>
					</p>
				</div>
				<Form
					form={form}
					layout="vertical"
					onFinish={async (values) => {
						// Handle form submission
						console.log("Values:", {
							address: values.address,
							coordinates: {
								lat: values.lat,
								lng: values.lng,
							},
							postalCode: values.postalCode,
							city: values.city,
							country: values.country,
						})

						// Call API to validate address
						const response = await API.put({
							url: Endpoints.uriMasterData(`validateData/${userId}`),
							data: {
								address: values.address,
								addressNotes: values.addressNotes,
								coordinates: {
									lat: values.lat,
									lng: values.lng,
								},
								postalCode: values.postalCode,
								city: values.city,
								country: values.country,
							},
						})

						// Handle API response
						if (response.ok) {
							notification.success({
								message: "Address Validation",
								description: "User address validated successfully",
								placement: "bottomRight",
								duration: 5,
							})
							refreshData()
							handleCancel()
						} else {
							notification.error({
								message: "Address Validation Error",
								description:
									response?.data?.message || Strings.serverErrors.wentWrong,
								placement: "bottomRight",
								duration: 5,
							})
						}
					}}
				>
					<div style={{ padding: "15px", paddingBottom: 0 }}>
						<div className="row">
							<Form.Item
								label="Address"
								name="address"
								rules={[{ required: true, message: "Required field" }]}
								className="col-12 mb-2"
							>
								<Autocomplete
									onLoad={(autocomplete) =>
										(autocompleteRef.current = autocomplete)
									}
									onPlaceChanged={handlePlaceChanged}
								>
									<Input
										value={address}
										onChange={(e) => {
											setAddress(e.target.value)
											form.setFieldsValue({
												address: e.target.value,
												lat: "",
												lng: "",
											})
											setCoordinates({})
										}}
									/>
								</Autocomplete>
							</Form.Item>

							<Form.Item
								label="Address Notes"
								name="addressNotes"
								className="col-12 mb-2"
							>
								<Input />
							</Form.Item>

							<Form.Item label="Latitude" name="lat" className="col-3 mb-2">
								<Input readOnly />
							</Form.Item>

							<Form.Item label="Longitude" name="lng" className="col-3 mb-2">
								<Input readOnly />
							</Form.Item>

							<Form.Item
								label="Postal Code"
								name="postalCode"
								className="col-3 mb-2"
							>
								<Input readOnly />
							</Form.Item>

							<Form.Item label="City" name="city" className="col-3 mb-2">
								<Input readOnly />
							</Form.Item>

							<Form.Item label="Country" name="country" className="col-12 mb-2">
								<Input readOnly />
							</Form.Item>

							<div className="col-12 mb-2 row">
								<Form.Item label="Errors" className="col-6 m-0">
									{userErrorsList?.length > 0 ? (
										<ul style={{ marginBottom: 0 }}>
											{userErrorsList.map((error: any) => (
												<li key={error} style={{ marginBottom: 5 }}>
													{translateUserWarningAndError(error)}
												</li>
											))}
										</ul>
									) : (
										<p>No errors!</p>
									)}
								</Form.Item>
							</div>
						</div>
					</div>

					{/* Map */}
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							padding: "15px",
							paddingTop: "0px",
						}}
					>
						<GoogleMaps
							loadScript={false}
							center={
								coordinates
									? {
											lat: coordinates?.lat,
											lng: coordinates?.lng,
										}
									: {
											lat: 0,
											lng: 0,
										}
							}
							zoom={coordinates ? 15 : 2}
							mapContainerStyle={{
								width: "100%",
								height: "360px",
								borderRadius: 5,
							}}
							options={{
								panControl: false,
							}}
							markers={
								coordinates && [
									{
										lat: coordinates?.lat,
										lng: coordinates?.lng,
									},
								]
							}
							polygons={
								userBoundingBox && [
									{
										paths: userBoundingBox,
									},
								]
							}
							onClick={{
								address: true,
								function: (location: {
									address?: string
									lat: number
									lng: number
									postalCode?: string
									city?: string
									country?: string
								}) => {
									// Set the address in the form
									form.setFieldsValue({
										address: location.address || "",
										lat: location.lat,
										lng: location.lng,
										postalCode: location.postalCode || "",
										city: location.city || "",
										country: location.country || "",
									})
									setAddress(location.address || "")
									setCoordinates({
										lat: location.lat,
										lng: location.lng,
									})
								},
							}}
						/>
					</div>

					<div
						className="UploadFooter"
						style={{
							display: "flex",
							justifyContent: "end",
							gap: 10,
						}}
					>
						<Button onClick={handleCancel}>{Strings.generic.cancel}</Button>
						<Button
							type="primary"
							htmlType="submit"
							disabled={
								address === "" ||
								!form.isFieldsTouched() ||
								address === user?.address?.address
							}
						>
							{/* Show validate address if address validation API need calling in backend */}
							{form.getFieldValue("address") &&
							form.getFieldValue("lat") &&
							form.getFieldValue("lng") &&
							form.getFieldValue("postalCode") &&
							form.getFieldValue("city") &&
							form.getFieldValue("country")
								? Strings.generic.save
								: "Validate Address"}
						</Button>
					</div>
				</Form>
			</div>
		</Modal>
	)
}

const translateUserWarningAndError = (key: string) => {
	switch (key) {
		case "postCode":
			return Strings.fields.zipCode || key
		case "address":
			return Strings.orders.address || key
		case "coordinates":
			return Strings.orders.coordinates || key
		default:
			return key
	}
}

export default AddressValidationModal
